import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { LogOut, UserCheck } from 'lucide-react';

import { clearAdminBackup, getImpersonator, stopImpersonation } from './impersonationService';

/**
 * Bandeau persistant affiché quand la session est impersonnée (claims
 * impersonator_* dans l'access token) : rappelle sous quelle identité on
 * navigue et offre le retour au compte admin.
 */
export const ImpersonationBanner: React.FC = () => {
  const auth = useAuth();
  const [leaving, setLeaving] = useState(false);

  const impersonator = useMemo(
    () => getImpersonator(auth.user?.access_token),
    [auth.user?.access_token],
  );

  // Session non impersonnée : purge le backup admin orphelin de cet onglet (logout
  // forcé…) — un token admin ne doit pas traîner. Le backup est en sessionStorage :
  // les autres onglets ne sont pas touchés, et la fermeture de l'onglet l'efface.
  useEffect(() => {
    if (auth.user && !impersonator) {
      clearAdminBackup();
    }
  }, [auth.user, impersonator]);

  if (!impersonator) return null;

  const currentName =
    auth.user?.profile?.name ?? auth.user?.profile?.preferred_username ?? 'un autre compte';

  const handleStop = async () => {
    setLeaving(true);
    await stopImpersonation();
  };

  return (
    <div className="sticky top-0 z-[60] flex items-center justify-center gap-3 bg-amber-500 px-4 py-1.5 text-sm font-medium text-white shadow">
      <UserCheck size={16} className="flex-shrink-0" />
      <span className="truncate">
        Connecté en tant que <strong>{currentName}</strong>
        {impersonator.userName ? ` (par ${impersonator.userName})` : ''}
      </span>
      <button
        type="button"
        onClick={handleStop}
        disabled={leaving}
        className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold hover:bg-white/30 disabled:opacity-60"
      >
        <LogOut size={12} />
        {leaving ? 'Retour…' : 'Revenir à mon compte'}
      </button>
    </div>
  );
};
